import { Fragment, useMemo, useState } from 'react';
import type { FuelingRecord } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useOverview } from '../features/hooks.js';
import { useDeleteFueling, useFueling, useSaveFueling } from '../features/fueling.js';
import { EmptyState, ErrorState, InlineError, LoadingState, Modal, PageHeader, SearchInput, Segmented, useConfirm } from '../components/ui.js';
import { IconTrash } from '../components/icons.js';
import { useFormatDate } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';
import { localTodayIso } from '../lib/dates.js';

const num = (n: number | null | undefined, unit = '') =>
  n == null ? '-' : `${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 2 })}${unit}`;

// ── add / edit modal ──────────────────────────────────────────────────────────
function FuelingModal({ initial, vessels, onClose }: { initial: FuelingRecord | null; vessels: { sap: string; name: string; model: string }[]; onClose: () => void }) {
  const save = useSaveFueling();
  const [f, setF] = useState({
    sap: initial?.sap ?? '',
    model: initial?.model ?? '',
    vesselName: initial?.vesselName ?? '',
    litres: initial?.litres != null ? String(initial.litres) : '',
    cost: initial?.cost != null ? String(initial.cost) : '',
    supplier: initial?.supplier ?? '',
    fueledOn: initial?.fueledOn ?? localTodayIso(),
    notes: initial?.notes ?? '',
  });
  const set = <K extends keyof typeof f>(k: K, v: string) => setF((p) => ({ ...p, [k]: v }));
  const valid = !!(f.sap.trim() || f.vesselName.trim());
  // the "SAP or vessel name" hint waits until the identity fields have been visited, so a fresh form
  // doesn't open on an error
  const [touched, setTouched] = useState(false);
  const touch = () => setTouched(true);

  const pickVessel = (sap: string) => {
    const v = vessels.find((x) => x.sap === sap);
    if (v) setF((p) => ({ ...p, sap: v.sap, vesselName: v.name, model: v.model }));
  };

  const submit = () =>
    save.mutate(
      {
        id: initial?.id,
        sap: f.sap, model: f.model, vesselName: f.vesselName,
        litres: f.litres === '' ? null : Number(f.litres),
        cost: f.cost === '' ? null : Number(f.cost),
        supplier: f.supplier, fueledOn: f.fueledOn, notes: f.notes,
      },
      { onSuccess: onClose },
    );

  return (
    <Modal
      title={initial ? 'Edit fueling record' : 'New fueling record'}
      width={560}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!valid || save.isPending} onClick={submit}>{save.isPending ? 'Saving…' : 'Save'}</button>
        </>
      }
    >
      {save.error && <InlineError error={save.error} onDismiss={() => save.reset()} />}
      {vessels.length > 0 && (
        <label>
          <span className="field-label">Pick vessel (auto-fills SAP / model / name)</span>
          <select className="field" style={{ width: '100%' }} value="" onChange={(e) => pickVessel(e.target.value)}>
            <option value="">- choose a vessel -</option>
            {vessels.map((v) => <option key={v.sap} value={v.sap}>{v.sap} · {v.name}{v.model ? ` (${v.model})` : ''}</option>)}
          </select>
        </label>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
        <div><label><span className="field-label">SAP</span><input className="field" inputMode="numeric" style={{ width: '100%' }} value={f.sap} onBlur={touch} onChange={(e) => set('sap', e.target.value)} /></label></div>
        <div><label><span className="field-label">Model</span><input className="field" style={{ width: '100%' }} value={f.model} onChange={(e) => set('model', e.target.value)} /></label></div>
        <div style={{ gridColumn: '1 / -1' }}><label><span className="field-label">Vessel name</span><input className="field" style={{ width: '100%' }} value={f.vesselName} onBlur={touch} onChange={(e) => set('vesselName', e.target.value)} /></label></div>
        <div><label><span className="field-label">Fuel added (litres)</span><input className="field" type="number" inputMode="decimal" style={{ width: '100%' }} value={f.litres} onChange={(e) => set('litres', e.target.value)} /></label></div>
        <div><label><span className="field-label">Cost (€)</span><input className="field" type="number" inputMode="decimal" style={{ width: '100%' }} value={f.cost} onChange={(e) => set('cost', e.target.value)} /></label></div>
        <div><label><span className="field-label">Date</span><input className="field" type="date" style={{ width: '100%' }} value={f.fueledOn} onChange={(e) => set('fueledOn', e.target.value)} /></label></div>
        <div><label><span className="field-label">Supplier / invoice ref</span><input className="field" style={{ width: '100%' }} value={f.supplier} onChange={(e) => set('supplier', e.target.value)} /></label></div>
        <div style={{ gridColumn: '1 / -1' }}><label><span className="field-label">Notes</span><input className="field" style={{ width: '100%' }} value={f.notes} onChange={(e) => set('notes', e.target.value)} /></label></div>
      </div>
      {!valid && touched && <div style={{ color: 'var(--err-fg)', fontSize: 11, marginTop: 8 }}>Enter a SAP or vessel name.</div>}
    </Modal>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────
export function Fueling() {
  const { can } = useAuth();
  const canEdit = can('fueling.edit');
  const { data, isLoading, isFetching, error, refetch } = useFueling();
  const overview = useOverview();
  const del = useDeleteFueling();
  const [confirmNode, confirmDlg] = useConfirm();
  const isPhone = useIsPhone();
  const fmtDate = useFormatDate();

  const [q, setQ] = useState('');
  const [view, setView] = useState<'date' | 'vessel'>('date');
  const [edit, setEdit] = useState<FuelingRecord | null | undefined>(undefined); // undefined=closed, null=new

  const vessels = useMemo(
    () => (overview.data?.vessels ?? []).map((v) => ({ sap: v.sap, name: v.name, model: v.model })).filter((v) => v.sap),
    [overview.data],
  );

  const rows = useMemo(() => {
    const query = fold(q.trim());
    const all = data?.records ?? [];
    const matched = !query ? all : all.filter((r) => hasText(query, r.sap, r.vesselName, r.model, r.supplier));
    // by date: newest first.
    return [...matched].sort((a, b) => (a.fueledOn < b.fueledOn ? 1 : a.fueledOn > b.fueledOn ? -1 : b.id - a.id));
  }, [data, q]);

  // by vessel: group records under their vessel, each group sorted by date (newest first).
  const groups = useMemo(() => {
    const m = new Map<string, { key: string; label: string; sap: string; model: string; records: FuelingRecord[]; litres: number; cost: number }>();
    for (const r of rows) {
      const key = (r.sap || r.vesselName || '-').toLowerCase();
      let g = m.get(key);
      if (!g) { g = { key, label: r.vesselName || r.sap || '-', sap: r.sap, model: r.model, records: [], litres: 0, cost: 0 }; m.set(key, g); }
      g.records.push(r);
      g.litres += r.litres ?? 0;
      g.cost += r.cost ?? 0;
    }
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const totalLitres = rows.reduce((a, r) => a + (r.litres ?? 0), 0);
  const totalCost = rows.reduce((a, r) => a + (r.cost ?? 0), 0);
  const euro = (n: number) => `€${n.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;

  const removeRecord = async (r: FuelingRecord) => {
    if (await confirmDlg({ title: 'Delete fueling record', message: 'Delete this fueling record?', confirmLabel: 'Delete', danger: true })) del.mutate(r.id);
  };

  // phones: one card per invoice (tbl-cards) — the date with "380 L · €702.30" on line 1, the boat, the
  // supplier / ref, the notes, then Edit / Delete. Under a vessel group the boat line is left out.
  const recordCard = (r: FuelingRecord, grouped: boolean) => (
    <tr key={r.id}>
      <td className="tc-title">{fmtDate(r.fueledOn) || r.fueledOn || '-'}</td>
      <td className="tc-end fuel-amt">{[r.litres != null && num(r.litres, ' L'), r.cost != null && euro(Number(r.cost))].filter(Boolean).join(' · ') || '-'}</td>
      {!grouped && <td>{[r.sap, r.model, r.vesselName].filter(Boolean).join(' · ') || '-'}</td>}
      {r.supplier && <td>{r.supplier}</td>}
      {r.notes && <td className="tc-meta">{r.notes}</td>}
      {canEdit && (
        <td className="tc-actions">
          <button className="btn btn-sm btn-ghost" onClick={() => setEdit(r)}>Edit</button>
          {/* Delete is a neutral ghost on every list (danger tint on hover / focus); the confirm carries the red */}
          <button className="btn btn-sm btn-ghost btn-del" disabled={del.isPending} onClick={() => void removeRecord(r)}><IconTrash size={14} /> Delete</button>
        </td>
      )}
    </tr>
  );

  const recordRow = (r: FuelingRecord, grouped = false) => isPhone ? recordCard(r, grouped) : (
    <tr key={r.id}>
      <td className="muted">{r.fueledOn || '-'}</td>
      <td>{r.sap || '-'}</td>
      <td>{r.model || '-'}</td>
      <td>{r.vesselName || '-'}</td>
      <td style={{ textAlign: 'right' }}>{num(r.litres, ' L')}</td>
      <td style={{ textAlign: 'right' }}>{r.cost == null ? '-' : euro(Number(r.cost))}</td>
      <td>{r.supplier || '-'}</td>
      <td className="muted" style={{ fontSize: 11 }}>{r.notes || ''}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {canEdit && <button className="btn btn-sm btn-ghost" onClick={() => setEdit(r)}>Edit</button>}
        {canEdit && <button className="btn btn-sm btn-ghost btn-del" style={{ marginLeft: 4 }} disabled={del.isPending} onClick={() => void removeRecord(r)}><IconTrash size={14} /> Delete</button>}
      </td>
    </tr>
  );

  return (
    <>
      <PageHeader title="Fueling Invoices">
        <SearchInput value={q} onChange={setQ} placeholder="Search SAP / vessel / supplier" width={240} />
        <Segmented
          value={view}
          onChange={setView}
          ariaLabel="Group records"
          options={[{ value: 'date', label: 'By date' }, { value: 'vessel', label: 'By vessel' }]}
        />
        <span className="muted page-count">{rows.length} record{rows.length === 1 ? '' : 's'} · {num(totalLitres, ' L')}{totalCost ? ` · ${euro(totalCost)}` : ''}</span>
        {canEdit && <button className="btn btn-sm btn-primary" onClick={() => setEdit(null)}>+ Fueling</button>}
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? 'Refreshing…' : 'Refresh'}</button>
      </PageHeader>

      {isLoading && <LoadingState label="Loading fueling records…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && rows.length === 0 && (q.trim()
        ? <EmptyState title="No records match" hint="Try a different SAP, vessel or supplier." />
        : <EmptyState title="No fueling records" hint={canEdit ? 'Use “+ Fueling” to log fuel added to a vessel.' : 'Nothing logged yet.'} />)}

      {data && rows.length > 0 && (
        <div className="tbl-wrap">
          <table className="tbl tbl-cards fuel-tbl">
            <thead>
              <tr><th>Date</th><th>SAP</th><th>Model</th><th>Vessel</th><th style={{ textAlign: 'right' }}>Litres</th><th style={{ textAlign: 'right' }}>Cost</th><th>Supplier / ref</th><th>Notes</th><th></th></tr>
            </thead>
            <tbody>
              {view === 'date' && rows.map((r) => recordRow(r))}
              {view === 'vessel' && groups.map((g) => (
                <Fragment key={g.key}>
                  {isPhone ? (
                    // the group heads its cards and carries their totals; the records drop the boat line
                    <tr className="fuel-grp">
                      <td className="tc-title">{g.label}<span className="fuel-grp-sub">{[g.sap, g.model].filter(Boolean).join(' · ')}{g.sap || g.model ? ' · ' : ''}{g.records.length} record{g.records.length === 1 ? '' : 's'}</span></td>
                      <td className="tc-end fuel-amt">{num(g.litres, ' L')}{g.cost ? ` · ${euro(g.cost)}` : ''}</td>
                    </tr>
                  ) : (
                    <tr style={{ background: 'var(--panel-alt)', fontWeight: 700 }}>
                      <td colSpan={4}>{g.label}{g.sap ? ` · ${g.sap}` : ''}{g.model ? ` · ${g.model}` : ''} <span className="muted" style={{ fontWeight: 400 }}>({g.records.length})</span></td>
                      <td style={{ textAlign: 'right' }}>{num(g.litres, ' L')}</td>
                      <td style={{ textAlign: 'right' }}>{g.cost ? euro(g.cost) : '-'}</td>
                      <td colSpan={3}></td>
                    </tr>
                  )}
                  {g.records.map((r) => recordRow(r, true))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                <td colSpan={4} style={{ textAlign: 'right' }}>Total</td>
                <td style={{ textAlign: 'right' }}>{num(totalLitres, ' L')}</td>
                <td style={{ textAlign: 'right' }}>{totalCost ? euro(totalCost) : '-'}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {edit !== undefined && <FuelingModal initial={edit} vessels={vessels} onClose={() => setEdit(undefined)} />}
      {confirmNode}
    </>
  );
}
